import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChatCircle, ChatsCircle, Check, Eye, PencilSimple, Plus, Trash } from '@phosphor-icons/react';
// Import the PURE reconnect logic directly (not the barrel / React hook) so the
// SPA's strict tsconfig doesn't drag the shared package's react / react-query
// deps through its own module resolution. This is the same shared client the
// app uses (§7.6) — the reconnect logic is what's shared; the thin hook wrapper
// is per-consumer.
import { connectChatSocket } from '@sportadmin/shared/realtime/socket';

import { config } from '@/config';
import { ApiError } from '@/api/client';
import {
  deleteConversation,
  listConversationsPage,
  markRead,
  fetchWsTicket,
  type Conversation,
} from '@/api/chat';
import { getMe } from '@/api/me';
import { listMyOrganizations } from '@/api/orgs';
import { Button } from '@/components/ui/button';
import { LoadError } from '@/components/LoadError';
import { EmptyState } from '@/components/ui/empty-state';
import {
  RowActions,
  StickyEndCell,
  useHorizontalScrollEdge,
  type RowAction,
} from '@/components/ui/row-actions';
import {
  TablePagination,
  useTablePaginationState,
} from '@/components/ui/table-pagination';
import { useConfirm } from '@/components/ConfirmDialog';
import { NewChatModal } from '@/components/chat/NewChatModal';
import { ChatThreadSheet } from '@/components/chat/ChatThreadSheet';
import { EditChatModal } from '@/components/chat/EditChatModal';
import { cn } from '@/lib/utils';

// Chat (mobile-app §7, §7.6): the admin SPA is a first-class chat surface, on
// the SAME backend + the SAME shared reconnecting WS client as the mobile app
// (@sportadmin/shared/realtime), so realtime can't drift. The page mirrors the
// other list pages (Teams / Members): a header with a "New chat" CTA, the
// shared empty-state card, and a list of conversations with a 3-dot actions
// column. Opening a conversation slides a thread panel over the list. Only
// admins/leaders can start a conversation (CanStartChat, enforced server-side);
// members/guardians can still read + reply to threads they're already in.

// Fixed leading icon + trailing actions columns; same trailing width as the
// other list tables so the rail aligns across pages.
const ICON_COL = '2.5rem';
const ACTIONS_COL = '4rem';
function gridTemplate(): string {
  return `${ICON_COL} minmax(180px, 2fr) minmax(120px, 1fr) ${ACTIONS_COL}`;
}

const START_CHAT_ROLES = new Set(['owner', 'administrator', 'leader']);

export function Chat() {
  const { slug = '' } = useParams();
  const { t, i18n } = useTranslation('common');
  const { user } = useAuth();
  const token = user?.access_token ?? null;
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const [newOpen, setNewOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);

  // The open thread is URL-driven (?conversation=<id>) — same pattern as the
  // Members page's ?member= panel — so the notification bell and the AI
  // assistant's navigate tool can deep-link straight into a conversation.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeId = searchParams.get('conversation');
  const setActiveId = useCallback(
    (id: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) next.set('conversation', id);
          else next.delete('conversation');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const { page, size, setPage, setSize, offset, limit } = useTablePaginationState({
    prefix: 'chat',
    defaultSize: 50,
  });

  const conversationsQuery = useQuery({
    queryKey: ['chat', 'conversations', slug, limit, offset],
    queryFn: () => listConversationsPage(slug, token, limit, offset),
    enabled: !!token && !!slug,
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteConversation(slug, id, token),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['chat', 'conversations', slug] });
    },
  });

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => getMe(token),
    enabled: !!token,
  });

  // Caller's role drives whether the "New chat" button renders. Reuses the
  // same query key the shell caches so there's no extra round-trip.
  const orgsQuery = useQuery({
    queryKey: ['my-orgs'],
    queryFn: () => listMyOrganizations(token),
    enabled: !!token,
  });
  const callerRole = orgsQuery.data?.find((o) => o.slug === slug)?.role ?? '';
  const canStartChat = START_CHAT_ROLES.has(callerRole);

  // The shared live socket — same reconnect logic the app uses. On a new
  // message we refetch the open thread + the inbox; on reconnect we refetch
  // everything (REST is authoritative, the socket is just faster).
  useEffect(() => {
    if (!slug) return undefined;
    const handle = connectChatSocket({
      baseUrl: config.apiURL,
      orgSlug: slug,
      getTicket: () => fetchWsTicket(slug, token),
      onFrame: (f) => {
        if (f.type === 'message.new') {
          void queryClient.invalidateQueries({ queryKey: ['chat', 'conversations', slug] });
          void queryClient.invalidateQueries({ queryKey: ['chat', 'messages', slug] });
          // Keep the header chat badge (ChatHeaderButton) in sync in real time.
          void queryClient.invalidateQueries({ queryKey: ['chat', 'unread', slug] });
        }
      },
      onReconnect: () => {
        void queryClient.invalidateQueries({ queryKey: ['chat', 'conversations', slug] });
      },
    });
    return () => handle.close();
  }, [slug, token, queryClient]);

  const conversations = conversationsQuery.data?.conversations ?? [];
  const total = conversationsQuery.data?.total ?? 0;
  const activeConversation = conversations.find((c) => c.id === activeId) ?? null;
  const editConversation = conversations.find((c) => c.id === editId) ?? null;
  const callerId = meQuery.data?.userId ?? null;
  const isOwner = (c: Conversation) => !!callerId && c.created_by_user_id === callerId;

  const resolveTitle = (c: Conversation): string =>
    c.title || (c.kind === 'group' ? t('chat.group') : t('chat.directMessage'));

  const isUnauthorized =
    conversationsQuery.isError &&
    conversationsQuery.error instanceof ApiError &&
    conversationsQuery.error.status === 404;

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 p-4 md:p-8">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('nav.chat')}
          </p>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight md:text-4xl">
            {t('chat.title')}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canStartChat && (
            <Button type="button" onClick={() => setNewOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('chat.newChat')}
            </Button>
          )}
        </div>
      </header>

      {conversationsQuery.isLoading && (
        <p className="text-sm text-muted-foreground">{t('chat.loading')}</p>
      )}

      {isUnauthorized && (
        <p
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
        >
          {t('chat.notFound')}
        </p>
      )}

      {conversationsQuery.isError && !isUnauthorized && (
        <LoadError onRetry={() => void conversationsQuery.refetch()} />
      )}

      {conversationsQuery.data && conversations.length === 0 && total === 0 && (
        <ChatEmpty {...(canStartChat ? { onStart: () => setNewOpen(true) } : {})} />
      )}

      {conversations.length > 0 && (
        <ConversationList
          conversations={conversations}
          resolveTitle={resolveTitle}
          locale={i18n.language}
          isOwner={isOwner}
          onOpen={setActiveId}
          onEdit={(id) => setEditId(id)}
          onDelete={(c) => {
            void (async () => {
              const ok = await confirm({
                title: t('chat.delete.title'),
                body: t('chat.delete.body', { title: resolveTitle(c) }),
                confirmLabel: t('chat.delete.cta'),
                tone: 'destructive',
              });
              if (ok) deleteMut.mutate(c.id);
            })();
          }}
          onMarkRead={(id) => {
            void markRead(slug, id, token).then(() => {
              void queryClient.invalidateQueries({ queryKey: ['chat', 'conversations', slug] });
              void queryClient.invalidateQueries({ queryKey: ['chat', 'unread', slug] });
            });
          }}
        />
      )}

      {total > 0 && (
        <TablePagination
          total={total}
          page={page}
          size={size}
          onPageChange={setPage}
          onSizeChange={setSize}
        />
      )}

      <NewChatModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        slug={slug}
        onCreated={(id) => setActiveId(id)}
      />

      <EditChatModal
        open={editId !== null}
        onClose={() => setEditId(null)}
        slug={slug}
        conversation={editConversation}
        title={editConversation ? resolveTitle(editConversation) : ''}
      />

      <ChatThreadSheet
        open={activeId !== null}
        onOpenChange={(open) => !open && setActiveId(null)}
        slug={slug}
        conversationId={activeId}
        conversation={activeConversation}
        title={activeConversation ? resolveTitle(activeConversation) : ''}
        currentUserId={callerId}
        isOwner={activeConversation ? isOwner(activeConversation) : false}
      />
    </div>
  );
}

function ChatEmpty({ onStart }: { onStart?: () => void }) {
  const { t } = useTranslation('common');
  return (
    <EmptyState
      icon={<ChatCircle weight="duotone" />}
      title={t('chat.empty.title')}
      body={t('chat.empty.body')}
      actions={
        onStart
          ? [
              {
                label: t('chat.newChat'),
                onClick: onStart,
                icon: <Plus weight="bold" />,
              },
            ]
          : undefined
      }
    />
  );
}

// formatLastMessage renders the inbox timestamp: 24h HH:MM today, medium date
// otherwise. Deterministic + locale-aware per the 24h-clock house rule.
function formatLastMessage(iso: string | null, locale: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  }
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(d);
}

function ConversationList({
  conversations,
  resolveTitle,
  locale,
  isOwner,
  onOpen,
  onEdit,
  onDelete,
  onMarkRead,
}: {
  conversations: Conversation[];
  resolveTitle: (c: Conversation) => string;
  locale: string;
  isOwner: (c: Conversation) => boolean;
  onOpen: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (c: Conversation) => void;
  onMarkRead: (id: string) => void;
}) {
  const { t } = useTranslation('common');
  const scrollRef = useRef<HTMLDivElement>(null);
  const elevated = useHorizontalScrollEdge(scrollRef);
  const template = gridTemplate();

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-border bg-background shadow-sm"
      style={{ clipPath: 'inset(0 round var(--radius-2xl))' }}
    >
      <div ref={scrollRef} className="overflow-x-auto">
        <div className="min-w-fit divide-y divide-border">
          <div
            className="hidden bg-muted/40 py-3 sm:grid"
            style={{ gridTemplateColumns: template }}
            role="row"
          >
            <span aria-hidden /> {/* icon col */}
            <span
              className="px-2 font-heading text-[0.75rem] font-semibold uppercase tracking-wider"
              role="columnheader"
            >
              {t('chat.column.conversation')}
            </span>
            <span
              className="px-2 font-heading text-[0.75rem] font-semibold uppercase tracking-wider"
              role="columnheader"
            >
              {t('chat.column.lastMessage')}
            </span>
            <StickyEndCell elevated={false} bgClass="bg-muted/40" className="justify-end">
              <span className="sr-only">{t('rowActions.columnHeader')}</span>
            </StickyEndCell>
          </div>

          {conversations.map((c) => (
            <ConversationRow
              key={c.id}
              conversation={c}
              title={resolveTitle(c)}
              locale={locale}
              template={template}
              elevated={elevated}
              isOwner={isOwner(c)}
              onOpen={() => onOpen(c.id)}
              onEdit={() => onEdit(c.id)}
              onDelete={() => onDelete(c)}
              onMarkRead={() => onMarkRead(c.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ConversationRow({
  conversation,
  title,
  locale,
  template,
  elevated,
  isOwner,
  onOpen,
  onEdit,
  onDelete,
  onMarkRead,
}: {
  conversation: Conversation;
  title: string;
  locale: string;
  template: string;
  elevated: boolean;
  isOwner: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onMarkRead: () => void;
}) {
  const { t } = useTranslation('common');
  const Icon = conversation.kind === 'group' ? ChatsCircle : ChatCircle;
  const hasUnread = conversation.unread > 0;

  const menuActions: RowAction[] = [
    {
      id: 'open',
      label: t('chat.actions.open'),
      icon: Eye,
      onSelect: onOpen,
    },
    ...(hasUnread
      ? [
          {
            id: 'mark-read',
            label: t('chat.actions.markRead'),
            icon: Check,
            onSelect: onMarkRead,
          },
        ]
      : []),
    ...(isOwner
      ? [
          {
            id: 'edit',
            label: t('chat.actions.edit'),
            icon: PencilSimple,
            onSelect: onEdit,
          },
          {
            id: 'delete',
            label: t('chat.actions.delete'),
            icon: Trash,
            onSelect: onDelete,
            destructive: true,
          },
        ]
      : []),
  ];

  return (
    <div
      className="group grid cursor-pointer items-stretch transition-colors hover:bg-muted/40"
      style={{ gridTemplateColumns: template }}
      role="row"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      {/* Conversation icon — single bubble for direct, stacked for group. */}
      <div className="flex items-center justify-center text-muted-foreground">
        <Icon className="h-5 w-5" weight="duotone" aria-hidden />
      </div>

      {/* Title + unread badge + member-count hint. */}
      <span className="flex items-center gap-2 px-2 py-3 text-sm" role="cell">
        <span
          className={cn(
            'truncate text-foreground',
            hasUnread ? 'font-heading font-semibold' : 'font-medium',
          )}
        >
          {title}
        </span>
        {conversation.member_count > 0 && (
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {t('chat.memberCount', { count: conversation.member_count })}
          </span>
        )}
        {hasUnread && (
          <span className="shrink-0 rounded-pill bg-primary px-2 py-0.5 text-[0.6875rem] font-semibold leading-none text-primary-foreground tabular-nums">
            {conversation.unread}
          </span>
        )}
      </span>

      {/* Last message time. */}
      <span
        className="flex items-center px-2 py-3 text-sm tabular-nums text-foreground/70"
        role="cell"
      >
        {formatLastMessage(conversation.last_message_at, locale)}
      </span>

      {/* Actions — stop propagation so opening the menu doesn't also open
          the thread behind it. */}
      <StickyEndCell elevated={elevated} bgClass="bg-background group-hover:bg-muted">
        <div onClick={(e) => e.stopPropagation()}>
          <RowActions menuActions={menuActions} />
        </div>
      </StickyEndCell>
    </div>
  );
}
